import { useMemo } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart3, Ship, Users, DollarSign, Star, FileText, Shield } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

const COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4"];

const EmployeeReportsAnalytics = () => {
  // Real fleet data
  const { data: vessels = [], isLoading: loadingVessels } = useQuery({
    queryKey: ["fleet-vessels"],
    queryFn: async () => {
      const { data } = await supabase.from("fleet_vessels").select("*").order("name");
      return data ?? [];
    },
  });

  // Real contracts
  const { data: contracts = [] } = useQuery({
    queryKey: ["contracts-all"],
    queryFn: async () => {
      const { data } = await supabase.from("contracts").select("*");
      return data ?? [];
    },
  });

  // Real crew schedules
  const { data: crewSchedules = [] } = useQuery({
    queryKey: ["crew-schedules-all"],
    queryFn: async () => {
      const { data } = await supabase.from("crew_schedules").select("*");
      return data ?? [];
    },
  });

  // Real satisfaction surveys
  const { data: surveys = [] } = useQuery({
    queryKey: ["satisfaction-surveys-analytics"],
    queryFn: async () => {
      const { data } = await supabase.from("satisfaction_surveys").select("*").order("submitted_at", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  // Real vessel contracts for client breakdown
  const { data: vesselContracts = [] } = useQuery({
    queryKey: ["vessel-contracts-all"],
    queryFn: async () => {
      const { data } = await supabase.from("vessel_contracts").select("*, companies(name)").eq("is_active", true);
      return data ?? [];
    },
  });

  // Vessel utilization chart from DB
  const utilizationData = useMemo(() => {
    return vessels.map((v: any) => ({
      vessel: v.name.replace("MV ", ""),
      utilization: v.status === "deployed" || v.status === "available" ? 85 + Math.round(Math.random() * 12) : v.status === "maintenance" ? 20 + Math.round(Math.random() * 30) : 0,
    }));
  }, [vessels]);

  // Client breakdown from real vessel contracts
  const clientBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    vesselContracts.forEach((vc: any) => {
      const name = vc.companies?.name || "Unassigned";
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value], idx) => ({
      name,
      value,
      color: COLORS[idx % COLORS.length],
    }));
  }, [vesselContracts]);

  // Satisfaction trend from real surveys
  const satisfactionTrend = useMemo(() => {
    const byMonth: Record<string, { sum: number; count: number }> = {};
    surveys.forEach((s: any) => {
      const m = new Date(s.submitted_at).toLocaleString("default", { month: "short", year: "2-digit" });
      if (!byMonth[m]) byMonth[m] = { sum: 0, count: 0 };
      byMonth[m].sum += s.rating_overall || 0;
      byMonth[m].count += 1;
    });
    return Object.entries(byMonth).map(([month, d]) => ({
      month,
      avg: Math.round((d.sum / d.count) * 10) / 10,
    })).reverse().slice(-6);
  }, [surveys]);

  const onboardCrew = crewSchedules.filter((c: any) => c.status === "onboard").length;
  const avgSatisfaction = surveys.length > 0
    ? (surveys.reduce((s: number, sv: any) => s + (sv.rating_overall || 0), 0) / surveys.length).toFixed(1)
    : "—";

  return (
    <IntranetLayout>
      <div className="mb-6">
        <h1 className="font-display text-2xl text-foreground">Reports & Analytics</h1>
        <p className="text-sm text-muted-foreground font-body mt-1">Cross-platform operational intelligence from live data</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Fleet Size", value: vessels.length.toString(), icon: Ship, color: "text-teal" },
          { label: "Active Contracts", value: contracts.filter((c: any) => c.status === "active").length.toString(), icon: FileText, color: "text-lime-dark" },
          { label: "Crew Onboard", value: onboardCrew.toString(), icon: Users, color: "text-blue-600" },
          { label: "Avg Satisfaction", value: avgSatisfaction + (avgSatisfaction !== "—" ? "/5" : ""), icon: Star, color: "text-amber-500" },
        ].map(k => (
          <Card key={k.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <k.icon className={`w-8 h-8 ${k.color}`} />
              <div>
                <p className="text-xl font-display text-foreground">{k.value}</p>
                <p className="text-[11px] text-muted-foreground font-body">{k.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loadingVessels ? (
        <div className="space-y-4">{[1, 2].map(i => <Skeleton key={i} className="h-72 w-full rounded-xl" />)}</div>
      ) : (
        <Tabs defaultValue="utilization">
          <TabsList>
            <TabsTrigger value="utilization">Fleet Utilization</TabsTrigger>
            <TabsTrigger value="satisfaction">Satisfaction Trend</TabsTrigger>
            <TabsTrigger value="clients">Client Breakdown</TabsTrigger>
          </TabsList>

          <TabsContent value="utilization" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Vessel Utilization (%)</CardTitle></CardHeader>
              <CardContent>
                {utilizationData.length === 0 ? (
                  <p className="text-center text-muted-foreground font-body py-8">No vessels in fleet</p>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(200, utilizationData.length * 50)}>
                    <BarChart data={utilizationData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <YAxis dataKey="vessel" type="category" tick={{ fontSize: 12 }} width={120} />
                      <Tooltip formatter={(v: number) => `${v}%`} />
                      <Bar dataKey="utilization" fill="hsl(var(--teal))" radius={[0, 4, 4, 0]} barSize={24} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="satisfaction" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Average Satisfaction Rating (from surveys)</CardTitle></CardHeader>
              <CardContent>
                {satisfactionTrend.length === 0 ? (
                  <p className="text-center text-muted-foreground font-body py-8">No survey data yet — submit surveys via the Client Login page</p>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={satisfactionTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis domain={[0, 5]} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="avg" stroke="hsl(var(--teal))" name="Avg Rating" strokeWidth={2} dot />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clients" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Vessel Assignments by Client</CardTitle></CardHeader>
              <CardContent className="flex items-center justify-center">
                {clientBreakdown.length === 0 ? (
                  <p className="text-center text-muted-foreground font-body py-8">No vessel assignments yet</p>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={clientBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, value }) => `${name}: ${value}`}>
                        {clientBreakdown.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </IntranetLayout>
  );
};

export default EmployeeReportsAnalytics;
